import {
    creativeReplayCompleteEventName,
    getFinishProcessingEvent,
    replayAllDescendantScriptsSerially,
    replayScriptTag,
    writeHtmlToDocumentRoot,
} from '@/client/render.js';

export const writeCreativeToDOM = async (htmlContent: string): Promise<void> => {
    const eventFired = waitForCreativeReplayComplete();
    documentWriteImpl(htmlContent, getFinishProcessingEvent());
    await eventFired;
};

const waitForCreativeReplayComplete = (): Promise<void> =>
    new Promise((resolve, reject) => {
        document.addEventListener(creativeReplayCompleteEventName, () => resolve(), { once: true });
        setTimeout(() => reject(new Error(`${creativeReplayCompleteEventName} timeout after 5 seconds`)), 5000);
    });

export const documentWriteImpl = (...text: string[]): void => {
    const htmlContent = text
        .filter((x) => !!x)
        .map(String)
        .join('');

    // The below conditional checks if an inline script is running AND that document has not
    // finished painting. In this case, document.write should append to the document immediately
    // below that script, as described here: https://www.oreilly.com/library/view/javascript-the-definitive/0596000480/re204.html
    // In all other cases we will replace the contents of the document with the string passed to document.write.
    if (document.currentScript !== null) {
        let currentElement: Element | null = document.currentScript;
        currentElement.insertAdjacentHTML('afterend', htmlContent);

        // Scripts do not execute when added via html string. Need to replay each script after the
        // insertion point (via next sibling). If we used replayAll on the body, it would infinitely
        // loop by replaying the script which called renderHTML in the first place.
        currentElement = currentElement.nextElementSibling;
        while (currentElement !== null) {
            if (currentElement.tagName === 'SCRIPT') {
                replayScriptTag(currentElement as HTMLScriptElement);
            } else {
                replayAllDescendantScriptsSerially(currentElement as HTMLElement);
            }
            currentElement = currentElement.nextElementSibling;
        }
    } else {
        writeHtmlToDocumentRoot(htmlContent);
        replayAllDescendantScriptsSerially(document.documentElement);
    }
};
